import { useCallback } from "react";
import { Delivery } from "../types/delivery";

interface CalculateNewSortRankParams {
  draggedDelivery: Delivery;
  dropIndex: number;
  allDeliveries: Delivery[];
}

interface CalculateSortRankResult {
  newSortRank: number;
  newDate?: Date | null;
  isValidDrop: boolean;
  errorMessage?: string;
}

const dateOnly = (date: Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

const isSameDate = (date1: Date | null, date2: Date | null): boolean => {
  if (date1 === null && date2 === null) return true;
  if (date1 === null || date2 === null) return false;
  return dateOnly(date1) === dateOnly(date2);
};

// Helper: get latest date between two dates
const getLatestDate = (date1: Date | null, date2: Date | null): Date | null => {
  if (date1 === null) return date2;
  if (date2 === null) return date1;
  return date1 > date2 ? date1 : date2;
};

/**
 * Hook to manage delivery reordering logic using before/after neighbors
 */
export function useDeliveryReorder({
  ascending = true,
  offset = 0,
}: {
  ascending?: boolean;
  offset?: number;
}) {
  // Calculate new sort_rank and date based on drop position
  const calculateNewSortRank = useCallback(
    ({
      draggedDelivery,
      dropIndex: rawDropIndex,
      allDeliveries,
    }: CalculateNewSortRankParams): CalculateSortRankResult => {
      // Find current drag index
      const dragIndex = allDeliveries.findIndex(
        (d) =>
          d.id === draggedDelivery.id && d.source === draggedDelivery.source,
      );

      // Since we removed the draggable, when dropping below it we need to shift the index by 1
      let dropIndex = rawDropIndex - offset;
      if (dropIndex >= dragIndex) {
        dropIndex += 1;
      }

      // Determine neighbors: delivery before and after drop position
      const before = dropIndex > 0 ? allDeliveries[dropIndex - 1] : null;
      const after: Delivery | null = allDeliveries[dropIndex] ?? null;

      // Case 1: Both neighbors have the same date, drop it in between of them
      if (before && after && isSameDate(before.date, after.date)) {
        return {
          /* Backend is designed to normalize ordering after every reorder
           * with a step of 10 between entries within same date. Hence,
           * taking an average between the two neighbors is expected to
           * never lead to a collision. */
          newSortRank: Math.floor((before.sortRank + after.sortRank) / 2),
          newDate: isSameDate(before.date, draggedDelivery.date)
            ? undefined
            : before.date,
          isValidDrop: true,
        };
      }

      // Case 2: Only before has the same date as dragged
      if (before && isSameDate(before.date, draggedDelivery.date)) {
        return {
          newSortRank: before.sortRank + (ascending ? 1 : -1),
          isValidDrop: true,
        };
      }

      // Case 3: Only after has the same date as dragged
      if (after && isSameDate(after.date, draggedDelivery.date)) {
        return {
          newSortRank: after.sortRank + (ascending ? -1 : 1),
          isValidDrop: true,
        };
      }

      // Case 4: Both neighbors have different dates from dragged
      // Set date to the latest of the two neighbors
      const latestDate = getLatestDate(
        before?.date || null,
        after?.date || null,
      );
      const rankSource = before && latestDate === before.date ? before : after;

      return {
        newSortRank: rankSource.sortRank - 1,
        newDate: latestDate,
        isValidDrop: true,
      };
    },
    [ascending, offset],
  );

  return {
    calculateNewSortRank,
  };
}
